import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  Tv,
  Building2,
  Key,
  Users,
  Menu,
  X,
  LogOut,
  ChevronRight,
  ChevronLeft
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

const navigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Dispositivos', href: '/devices', icon: Tv },
  { name: 'Empresas', href: '/companies', icon: Building2 },
  { name: 'Licencias', href: '/licenses', icon: Key },
  { name: 'Usuarios', href: '/users', icon: Users },
]

const Sidebar = ({ onExpandChange }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const location = useLocation()
  const { user, logout } = useAuth()

  // Notificar al Layout cuando cambie el estado
  useEffect(() => {
    if (onExpandChange) {
      onExpandChange(!isCollapsed)
    }
  }, [isCollapsed, onExpandChange])

  const toggleCollapse = () => {
    console.log('Toggle clicked! Current state:', isCollapsed) // Debug
    setIsCollapsed(!isCollapsed)
  }

  return (
    <>
      {/* Sidebar móvil */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-900/80" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 z-50 w-72 bg-gradient-to-b from-red-900 to-red-800">
          <div className="flex h-full flex-col">
            <div className="flex h-16 shrink-0 items-center justify-center px-6">
              <img
                src="/assets/img/Captura de pantalla 2025-07-18 095525.jpg"
                alt="JCB Technology"
                className="h-12 w-auto object-contain"
              />
            </div>
            <nav className="flex flex-1 flex-col px-6 pb-4">
              <ul className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul className="-mx-2 space-y-1">
                    {navigation.map((item) => {
                      const isActive = location.pathname === item.href
                      return (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                              ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-lg'
                              : 'text-red-200 hover:text-white hover:bg-red-700/50'
                              }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <item.icon className="h-5 w-5 mr-3" />
                            {item.name}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </li>
                <li className="mt-auto">
                  <div className="flex items-center gap-x-4 px-3 py-3 text-sm font-medium text-red-200">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-red-600 to-orange-500 text-white font-bold">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <span>{user?.name}</span>
                  </div>
                  <button
                    onClick={logout}
                    className="flex w-full items-center gap-x-4 px-3 py-2.5 text-sm font-medium text-red-200 hover:text-white hover:bg-red-700/50 rounded-lg transition-all duration-200"
                  >
                    <LogOut className="h-5 w-5" />
                    Cerrar sesión
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Sidebar desktop - JCB COLORS */}
      <div className="hidden lg:block lg:fixed lg:inset-y-0 lg:z-50">
        <div className={`h-full bg-gradient-to-b from-red-900 to-red-800 transition-all duration-300 ease-in-out relative ${isCollapsed ? 'w-16' : 'w-64'
          }`}>

          {/* Botón toggle elegante */}
          <button
            onClick={toggleCollapse}
            className="absolute top-3 right-3 text-red-200 bg-red-700/50 hover:bg-red-600/50 rounded-lg p-2 transition-all duration-200 border border-red-600/30 z-50"
            title={isCollapsed ? "Expandir" : "Contraer"}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>

          {/* Header JCB */}
          <div className="flex h-20 items-center justify-center px-4 pt-12">
            {!isCollapsed && (
              <img
                src="/assets/img/Captura de pantalla 2025-07-18 095525.jpg"
                alt="JCB Technology"
                className="h-14 w-auto object-contain"
              />
            )}
          </div>

          {/* Navegación JCB */}
          <nav className="flex-1 px-4 py-4">
            <ul className="space-y-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                          ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-lg'
                          : 'text-red-200 hover:text-white hover:bg-red-700/50'
                        } ${isCollapsed ? 'justify-center' : ''}`}
                      title={isCollapsed ? item.name : undefined}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {!isCollapsed && <span className="ml-3">{item.name}</span>}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </nav>

          {/* Usuario y logout JCB */}
          <div className="px-4 pb-4 border-t border-red-700/50 pt-4">
            {!isCollapsed ? (
              <>
                <div className="flex items-center gap-x-3 px-3 py-3 text-sm font-medium text-red-200">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-red-600 to-orange-500 text-white font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <span>{user?.name}</span>
                </div>
                <button
                  onClick={logout}
                  className="flex w-full items-center gap-x-3 px-3 py-2.5 text-sm font-medium text-red-200 hover:text-white hover:bg-red-700/50 rounded-lg transition-all duration-200"
                >
                  <LogOut className="h-5 w-5" />
                  Cerrar sesión
                </button>
              </>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-center">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-red-600 to-orange-500 text-white font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                </div>
                <button
                  onClick={logout}
                  className="flex w-full justify-center p-2.5 text-sm font-medium text-red-200 hover:text-white hover:bg-red-700/50 rounded-lg transition-all duration-200"
                  title="Cerrar sesión"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Botón móvil */}
      <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <span className="sr-only">Abrir sidebar</span>
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-center">
          <img
            src="/assets/img/Captura de pantalla 2025-07-18 095525.jpg"
            alt="JCB Technology"
            className="h-8 w-auto object-contain"
          />
        </div>
      </div>
    </>
  )
}

export default Sidebar 